import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiX, FiStar, FiPlus, FiSearch, FiBriefcase, FiTool, FiActivity, FiUsers } from 'react-icons/fi';
import { mapInputToSkills } from '../../../services/skillMapping';
import { USER_PROFICIENCY_LEVELS } from '../../../utils/proficiencyMap';
import { apiClient } from '../../../services/api';

interface DetectedSkill {
    name: string;
    category: 'professional' | 'practical' | 'soft' | 'physical';
    confidence: number;
    status: 'pending' | 'accepted' | 'rejected';
    proficiency: number;
}

interface SystemSkill {
    _id: string;
    name: string;
    category: string;
    description: string;
}

interface CapabilityStepProps {
    discoveryData: {
        enjoyDoing: string[];
        haveDone: string[];
        wantNow: string[];
    };
    data: {
        confirmedSkills: Array<{
            name: string;
            category: string;
            proficiency: number;
        }>;
    };
    onUpdate: (data: any) => void;
    onNext: () => void;
    onBack: () => void;
}

const CapabilityStep: React.FC<CapabilityStepProps> = ({
    discoveryData,
    data,
    onUpdate,
    onNext,
    onBack
}) => {
    const [detectedSkills, setDetectedSkills] = useState<DetectedSkill[]>([]);
    const [systemSkills, setSystemSkills] = useState<SystemSkill[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        // Fetch all available system skills for manual search
        const fetchSkills = async () => {
            try {
                const response = await apiClient.getAvailableSkills();
                const skills = Array.isArray(response) ? response : (response.skills || []);
                setSystemSkills(skills);
            } catch (err) {
                console.error('Failed to fetch system skills:', err);
            }
        };
        fetchSkills();
    }, []);

    useEffect(() => {
        // Map all discovery inputs to skills
        const allInputs = [
            ...discoveryData.enjoyDoing,
            ...discoveryData.haveDone,
            ...discoveryData.wantNow
        ];

        const skillsMap = new Map<string, DetectedSkill>();

        // First, add already confirmed skills to the map to keep state consistent
        (data.confirmedSkills || []).forEach(skill => {
            skillsMap.set(skill.name, {
                name: skill.name,
                category: skill.category as any,
                confidence: 1,
                status: 'accepted',
                proficiency: skill.proficiency
            });
        });

        // Then detect others
        allInputs.forEach(input => {
            if (!input.trim()) return;
            const mapping = mapInputToSkills(input);
            mapping.detectedSkills.forEach(skill => {
                if (!skillsMap.has(skill.name)) {
                    skillsMap.set(skill.name, {
                        ...skill,
                        status: 'pending',
                        proficiency: 2 // Default to "Capable"
                    });
                }
            });
        });

        setDetectedSkills(Array.from(skillsMap.values()));
    }, [discoveryData]);

    const handleAccept = (skillName: string) => {
        const skill = detectedSkills.find(s => s.name === skillName);
        if (!skill) return;

        setDetectedSkills(prev =>
            prev.map(s =>
                s.name === skillName ? { ...s, status: 'accepted' as const } : s
            )
        );

        // Add to confirmed skills
        const confirmed = data.confirmedSkills || [];
        if (!confirmed.find(s => s.name === skillName)) {
            onUpdate({
                confirmedSkills: [
                    ...confirmed,
                    {
                        name: skill.name,
                        category: skill.category,
                        proficiency: skill.proficiency
                    }
                ]
            });
        }
    };

    const handleAddManualSkill = (skill: SystemSkill | { name: string; category: string }) => {
        const newSkill: DetectedSkill = {
            name: skill.name,
            category: (skill.category as any) || 'professional',
            confidence: 1,
            status: 'accepted',
            proficiency: 2
        };

        setDetectedSkills(prev => {
            if (prev.find(s => s.name === newSkill.name)) {
                return prev.map(s => s.name === newSkill.name ? { ...s, status: 'accepted' as const } : s);
            }
            return [...prev, newSkill];
        });

        const confirmed = data.confirmedSkills || [];
        if (!confirmed.find(s => s.name === newSkill.name)) {
            onUpdate({
                confirmedSkills: [
                    ...confirmed,
                    {
                        name: newSkill.name,
                        category: newSkill.category,
                        proficiency: newSkill.proficiency
                    }
                ]
            });
        }
        setSearchQuery('');
        setIsSearching(false);
    };

    const handleReject = (skillName: string) => {
        setDetectedSkills(prev =>
            prev.map(s =>
                s.name === skillName ? { ...s, status: 'rejected' as const } : s
            )
        );

        // Remove from confirmed skills
        onUpdate({
            confirmedSkills: (data.confirmedSkills || []).filter(s => s.name !== skillName)
        });
    };

    const handleProficiencyChange = (skillName: string, proficiency: number) => {
        setDetectedSkills(prev =>
            prev.map(s =>
                s.name === skillName ? { ...s, proficiency } : s
            )
        );

        // Update in confirmed skills
        onUpdate({
            confirmedSkills: (data.confirmedSkills || []).map(s =>
                s.name === skillName ? { ...s, proficiency } : s
            )
        });
    };

    const pendingSkills = detectedSkills.filter(s => s.status === 'pending');
    const acceptedSkills = detectedSkills.filter(s => s.status === 'accepted');
    const canProceed = acceptedSkills.length > 0;

    const categoryColors: Record<string, string> = {
        professional: '#D4AF37',
        practical: '#4A90E2',
        physical: '#7ED321',
        soft: '#9013FE'
    };

    const filteredSearchSkills = searchQuery.trim()
        ? systemSkills.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : [];

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-white mb-2 font-serif">
                    Confirm Your Masteries
                </h2>
                <p className="text-white/60">
                    Search for specific masteries or review recommendations based on your profile inputs.
                </p>
            </div>

            {/* Manual Search Bar */}
            <div className="relative">
                <div className="flex items-center gap-3 p-1 bg-white/5 border border-white/10 rounded-xl focus-within:border-premium-gold/50 transition-all">
                    <div className="pl-3 text-white/40">
                        <FiSearch size={20} />
                    </div>
                    <input
                        type="text"
                        placeholder="Search or add any skill manually..."
                        value={searchQuery}
                        onFocus={() => setIsSearching(true)}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent py-3 text-white outline-none placeholder:text-white/20 text-sm"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="p-2 text-white/40 hover:text-white"
                        >
                            <FiX size={18} />
                        </button>
                    )}
                </div>

                {/* Search Results Dropdown */}
                <AnimatePresence>
                    {isSearching && (searchQuery || filteredSearchSkills.length > 0) && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute top-full left-0 right-0 mt-2 bg-[#1a1510] border border-white/10 rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto custom-scrollbar"
                        >
                            {filteredSearchSkills.map(skill => (
                                <button
                                    key={skill._id}
                                    onClick={() => handleAddManualSkill(skill)}
                                    className="w-full px-4 py-3 text-left hover:bg-white/5 flex items-center justify-between group border-b border-white/5 last:border-0"
                                >
                                    <div>
                                        <div className="text-white font-bold text-sm">{skill.name}</div>
                                        <div className="text-[10px] text-white/40 uppercase tracking-widest">{skill.category}</div>
                                    </div>
                                    <FiPlus className="text-white/20 group-hover:text-premium-gold transition-colors" />
                                </button>
                            ))}
                            {searchQuery && !filteredSearchSkills.find(s => s.name.toLowerCase() === searchQuery.toLowerCase()) && (
                                <button
                                    onClick={() => handleAddManualSkill({ name: searchQuery, category: 'professional' })}
                                    className="w-full px-4 py-4 text-left hover:bg-premium-gold/10 flex items-center gap-3 text-premium-gold group"
                                >
                                    <FiPlus size={20} />
                                    <div className="text-sm font-bold">Add "{searchQuery}" as a custom skill</div>
                                </button>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Pending Suggestions */}
            {pendingSkills.length > 0 && (
                <div className="space-y-3">
                    <h3 className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-premium-gold animate-pulse" />
                        Recommendations for Review
                    </h3>
                    <div className="grid gap-3">
                        {pendingSkills.map((skill, index) => (
                            <motion.div
                                key={skill.name}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="p-4 bg-white/5 border border-white/10 rounded-xl group hover:border-white/20 transition-all"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center border border-white/10">
                                            {skill.category === 'professional' && <FiBriefcase className="text-premium-gold" />}
                                            {skill.category === 'practical' && <FiTool className="text-premium-gold" />}
                                            {skill.category === 'physical' && <FiActivity className="text-premium-gold" />}
                                            {skill.category === 'soft' && <FiUsers className="text-premium-gold" />}
                                        </div>
                                        <div>
                                            <h4 className="text-white font-bold">{skill.name}</h4>
                                            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">
                                                {skill.category}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleAccept(skill.name)}
                                            className="p-2.5 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-all border border-green-500/20"
                                            title="Yes, I have this"
                                        >
                                            <FiCheck size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleReject(skill.name)}
                                            className="p-2.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-all border border-red-500/20"
                                            title="No, skip this"
                                        >
                                            <FiX size={18} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {/* Accepted Skills with Proficiency */}
            {acceptedSkills.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                    <h3 className="text-[10px] font-bold text-green-400 uppercase tracking-[0.2em]">
                        ✓ Confirmed Masteries ({acceptedSkills.length})
                    </h3>
                    <div className="space-y-4">
                        {acceptedSkills.map((skill) => (
                            <motion.div
                                key={skill.name}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="p-6 bg-green-500/5 border border-green-500/20 rounded-2xl relative overflow-hidden group"
                            >
                                <div className="absolute top-0 left-0 w-1 h-full bg-green-500/50" />

                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center border border-green-500/20">
                                            <FiCheck className="text-green-400" size={18} />
                                        </div>
                                        <div>
                                            <h4 className="text-white font-bold font-serif text-lg">{skill.name}</h4>
                                            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/40">
                                                {skill.category} Specialty
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleReject(skill.name)}
                                        className="text-white/20 hover:text-red-400 p-2 transition-colors rounded-lg hover:bg-white/5"
                                        title="Remove Mastery"
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>

                                {/* Proficiency Selector */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <p className="text-[10px] font-bold text-premium-gold uppercase tracking-widest">Aptitude Level</p>
                                        <span className="text-[10px] text-white/40 italic">
                                            {USER_PROFICIENCY_LEVELS.find(l => l.value === skill.proficiency)?.userLabel}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-2">
                                        {USER_PROFICIENCY_LEVELS.map((level) => (
                                            <button
                                                key={level.value}
                                                onClick={() => handleProficiencyChange(skill.name, level.value)}
                                                className={`p-3 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${skill.proficiency === level.value
                                                    ? 'bg-premium-gold text-[#1a1510] border border-premium-gold shadow-[0_0_15px_rgba(212,175,55,0.3)]'
                                                    : 'bg-white/5 text-white/30 hover:bg-white/10 border border-white/5'
                                                    }`}
                                            >
                                                <div className="flex justify-center mb-1.5 gap-0.5">
                                                    {Array.from({ length: level.value }).map((_, i) => (
                                                        <FiStar
                                                            key={i}
                                                            size={8}
                                                            className={skill.proficiency === level.value ? 'fill-[#1a1510]' : 'fill-premium-gold opacity-50'}
                                                        />
                                                    ))}
                                                </div>
                                                {level.userLabel}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[11px] text-white/40 leading-relaxed pt-1">
                                        {USER_PROFICIENCY_LEVELS.find(l => l.value === skill.proficiency)?.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-8">
                <button
                    onClick={onBack}
                    className="px-6 py-2 text-white/40 hover:text-white transition-colors font-bold uppercase tracking-widest text-xs"
                >
                    ← Back
                </button>
                <button
                    onClick={onNext}
                    disabled={!canProceed}
                    className={`px-10 py-4 rounded-xl font-bold uppercase tracking-widest text-sm transition-all ${canProceed
                        ? 'bg-gradient-to-r from-premium-gold-dark to-premium-gold text-[#1a1510] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transform hover:scale-105'
                        : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'
                        }`}
                >
                    Continue to Experience →
                </button>
            </div>
        </div>
    );
};

export default CapabilityStep;
